import { Language } from "@mui/icons-material";
import { Box, Button, Card, CardContent, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { GB, IT, SI } from 'country-flag-icons/react/3x2';
import I18n, { changeLanguage } from 'i18next';
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Background from "../../assets/background.png";
import LoginLogo from "../../assets/beetogether-logo-520x520.png";
import CustomForm from "../../common/components/CustomForm";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import { getForm } from "../../common/config/forms";
import { IRegisterBody } from "../../common/interfaces/auth";
import { ICustomOption } from "../../common/interfaces/form";
import areaExpertiseService from "../../common/services/areaExpertiseService";
import authService from "../../common/services/authService";

interface IRegisterProps { }

const Register: FC<IRegisterProps> = () => {
    // hooks
    const locale = I18n.language;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
    const [language, setLanguage] = useState(locale);

    const lang = [
        { flag: <GB title="English" />, label: I18n.t("EN", { locale: language }), value: "en-EN" },
        { flag: <IT title="Italy" />, label: I18n.t("IT", { locale: language }), value: "it-IT" },
        { flag: <SI title="Slovenian" />, label: I18n.t("SL", { locale: language }), value: "sl-SI" },
    ]

    // form
    const signUpForm = getForm<IRegisterBody>("register");
    const [areasOptions, setAreasOptions] = useState<ICustomOption[]>([]);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">(
        "success"
    );

    const getAreasList = async () => {
        try {
            const response = await areaExpertiseService.getAllForRegister();
            let options = response.content.map(area => {
                return {
                    value: area.id,
                    desc: area.name
                }
            })
            setAreasOptions(options)
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessage(error.response?.data.title)
            } else {
                setMessage(t("genericError"));
            }
        }
    }

    useEffect(() => {
        getAreasList()
    }, [])

    const handleRegister = async (data: IRegisterBody & { confirmPassword: string, beehiveAssigned: boolean }) => {
        const { confirmPassword, beehiveAssigned, ...registerBody } = data
        try {
            await authService.register(registerBody)
            navigate("/login", { state: { from: "register" } });
        } catch (error) {
            if (error instanceof AxiosError) {
                setMessage(error.response?.data.content.message);
            } else {
                setMessage(t("genericError"));
            }
            setResponseType("error");
            setOpenSnackbar(true);
        }
    };

    const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLang(event.currentTarget);
    };

    const handleCloseLangMenu = (option: { label: string, value: string }) => {
        if (locale !== option.value) {
            setLanguage(option.value)
            changeLanguage(option.value);
        }
        setAnchorElLang(null);
    };

    const initialValuesSignUp: IRegisterBody & { confirmPassword: string, beehiveAssigned: boolean } = {
        firstName: "",
        lastName: "",
        emailAddress: "",
        password: "",
        confirmPassword: "",
        beehiveAssigned: false,
        areaId: ""
    };

    return <Box sx={
        {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundRepeat: "none",
            backgroundSize: "cover",
            backgroundImage: `url(${Background})`,
            backgroundPosition: "center"
        }
    }>
        <CustomSnackbar type={responseType} message={message} open={openSnackbar} onClose={() => setOpenSnackbar(false)} />
        <Card sx={{ width: 380, p: 1, m: 2, position: "relative" }}>
            <Box>
                <Button variant="outlined" onClick={() => navigate("/login")} sx={{ position: "absolute" }}>
                    {t("navigateBack")}
                </Button>
            </Box>
            <Tooltip title={t("header.selectLanguage")} sx={{ position: "absolute", top: 8, right: 12 }}>
                <IconButton onClick={handleOpenLangMenu} color="primary">
                    <Language />
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElLang}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElLang)}
                onClose={handleCloseLangMenu}
            >
                {lang.map((option) => (
                    <MenuItem key={option.label} onClick={() => handleCloseLangMenu(option)}>
                        <Box sx={{
                            width: 28,
                            height: 19,
                            mr: 1
                        }}>{option.flag}</Box>
                        <Typography textAlign="center">{option.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
            <Box sx={{ display: "flex", justifyContent: "center", m: 2, mb: 0 }}>
                <Box
                    component="img"
                    sx={{ height: "160px" }}
                    src={LoginLogo}
                    title="bee2gether-logo"
                ></Box>
            </Box>
            <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                <Typography gutterBottom variant="h6" textAlign="center" component="div">
                    {t("register.title")}
                </Typography>
                <CustomForm
                    {...signUpForm}
                    inputs={[
                        ...signUpForm.inputs.map(input => {
                            if (input.name === "areaId")
                                return { ...input, options: areasOptions }
                            else
                                return input
                        })
                    ]}
                    initialValues={initialValuesSignUp}
                    submit={handleRegister}
                    labelButtonSubmit={t("register.submitRegister")}
                    customStyles={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                    isDialog={false}
                />
            </CardContent>
        </Card>
    </Box>
}

export default Register;